import React, { useState, useRef } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Avatar,
  Typography,
  Chip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PlayerScoresModal from './PlayerScoresModal';

const RANKINGS_PER_PANE = 5;

function RankingsCarousel({ bars, onUpdate, playerScores = [] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const getRankColor = (rank) => {
    if (rank === 0) return '#FFD700'; // Gold
    if (rank === 1) return '#C0C0C0'; // Silver
    if (rank === 2) return '#CD7F32'; // Bronze
    return 'primary.main';
  };

  const getRankIcon = (rank) => {
    if (rank < 3) {
      return <EmojiEventsIcon sx={{ fontSize: 20, color: getRankColor(rank) }} />;
    }
    return null;
  };

  if (playerScores.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No scores yet. Be the first to play!
        </Typography>
      </Box>
    );
  }

  // Calculate total items and max index
  const totalItems = playerScores.length;
  const totalPanes = Math.ceil(totalItems / RANKINGS_PER_PANE);
  const maxIndex = Math.max(0, totalPanes - 1);

  const handlePrevious = () => {
    if (totalPanes === 0) return;
    setCurrentIndex(prev => {
      // Loop to last pane if at first
      return prev === 0 ? maxIndex : prev - 1;
    });
  };

  const handleNext = () => {
    if (totalPanes === 0) return;
    setCurrentIndex(prev => {
      // Loop to first pane if at last
      return prev === maxIndex ? 0 : prev + 1;
    });
  };

  // Swipe gesture handlers
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrevious();
    }
  };

  // Get visible rankings for current pane
  const startIndex = currentIndex * RANKINGS_PER_PANE;
  const endIndex = Math.min(startIndex + RANKINGS_PER_PANE, totalItems);
  const visibleRankings = playerScores.slice(startIndex, endIndex);

  return (
    <Box sx={{ width: '100%', position: 'relative', pt: 2 }}>
      <Box
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          touchAction: 'pan-y',
          overflow: 'hidden',
          px: { xs: 2, sm: 4 },
        }}
      >
        {/* Left Arrow */}
        {totalPanes > 1 && (
          <IconButton
            onClick={handlePrevious}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
              },
              flexShrink: 0,
              zIndex: 2,
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}

        {/* Rankings Pane */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            flex: 1,
            minWidth: 0,
            width: '100%',
            pt: '5px',
          }}
        >
          {visibleRankings.map((player, paneIndex) => {
            const globalIndex = startIndex + paneIndex;
            return (
              <Paper
                key={player.name}
                elevation={globalIndex < 3 ? 4 : 2}
                onClick={() => {
                  setSelectedPlayer(player);
                  setModalOpen(true);
                }}
                sx={{
                  p: 2,
                  width: '100%',
                  height: '100%',
                  minHeight: 100,
                  border: globalIndex < 3 ? 2 : 1,
                  borderColor: globalIndex < 3 ? getRankColor(globalIndex) : 'divider',
                  backgroundColor: 'rgba(102, 126, 234, 0.1)',
                  display: 'flex',
                  flexDirection: 'column',
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(102, 126, 234, 0.15)',
                    boxShadow: globalIndex < 3 ? 6 : 4,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={2} sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: '50%',
                      backgroundColor: globalIndex < 3 ? getRankColor(globalIndex) : 'primary.dark',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '1.2rem',
                    }}
                  >
                    {globalIndex + 1}
                  </Box>
                  <Avatar
                    src={player.avatar}
                    alt={player.name}
                    sx={{
                      width: 48,
                      height: 48,
                      flexShrink: 0,
                      border: '2px solid',
                      borderColor: globalIndex < 3 ? getRankColor(globalIndex) : 'primary.main',
                    }}
                  >
                    {player.name?.[0]}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.2,
                        mb: 0.5,
                      }}
                    >
                      {player.name}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Chip
                        label={`Total: ${player.totalScore}`}
                        size="small"
                        color="primary"
                        sx={{ 
                          fontWeight: 600,
                        }}
                      />
                      {player.photoCount > 0 && (
                        <Chip
                          label={`${player.photoCount} photo${player.photoCount !== 1 ? 's' : ''}`}
                          size="small"
                          sx={{ 
                            fontWeight: 500,
                            backgroundColor: 'rgba(102, 126, 234, 0.2)',
                            color: '#8fa3f5',
                          }}
                        />
                      )}
                      {getRankIcon(globalIndex)}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>

        {/* Right Arrow */}
        {totalPanes > 1 && (
          <IconButton
            onClick={handleNext}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
              },
              flexShrink: 0,
              zIndex: 2,
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        )}
      </Box>

      {/* Carousel Indicators */}
      {totalPanes > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            mt: 3,
            pt: 2,
          }}
        >
          {Array.from({ length: totalPanes }).map((_, paneIndex) => {
            const isActive = paneIndex === currentIndex;
            
            return (
              <Box
                key={paneIndex}
                onClick={() => setCurrentIndex(paneIndex)}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: isActive
                    ? 'white'
                    : 'rgba(255, 255, 255, 0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  },
                }}
              />
            );
          })}
        </Box>
      )}

      <PlayerScoresModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedPlayer(null);
        }}
        player={selectedPlayer}
        bars={bars}
        onUpdate={onUpdate}
      />
    </Box>
  );
}

export default RankingsCarousel;

